import { Heart } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/common/ui/card";
import { Skeleton } from "@/common/ui/skeleton";

import type { WhoopRecovery } from "../devices-types";

interface RecoveryCardProps {
  recovery: WhoopRecovery[];
  isLoading: boolean;
}

function recoveryColor(score: number): string {
  if (score >= 67) return "text-green-600";
  if (score >= 34) return "text-yellow-600";
  return "text-red-600";
}

export function RecoveryCard({ recovery, isLoading }: RecoveryCardProps): React.ReactElement {
  const latest = recovery[0];

  return (
    <Card data-testid="recovery-card">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">Recovery</CardTitle>
        <Heart className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-20 w-full" />
        ) : latest ? (
          <div className="space-y-3">
            <div
              data-testid="recovery-score"
              className={`text-3xl font-bold ${recoveryColor(latest.score.recovery_score)}`}
            >
              {latest.score.recovery_score}%
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-muted-foreground">HRV</span>
                <div data-testid="recovery-hrv" className="font-medium">
                  {latest.score.hrv_rmssd_milli.toFixed(1)} ms
                </div>
              </div>
              <div>
                <span className="text-muted-foreground">RHR</span>
                <div data-testid="recovery-rhr" className="font-medium">
                  {latest.score.resting_heart_rate} bpm
                </div>
              </div>
              {latest.score.spo2_percentage !== null && (
                <div>
                  <span className="text-muted-foreground">SpO2</span>
                  <div data-testid="recovery-spo2" className="font-medium">
                    {latest.score.spo2_percentage}%
                  </div>
                </div>
              )}
              {latest.score.skin_temp_celsius !== null && (
                <div>
                  <span className="text-muted-foreground">Skin Temp</span>
                  <div data-testid="recovery-temp" className="font-medium">
                    {latest.score.skin_temp_celsius}°C
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No recovery data available</p>
        )}
      </CardContent>
    </Card>
  );
}
